import { F0Form, f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import { useEffect } from 'react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import useSubmissionPeriods from 'modules/planning/hooks/useSubmissionPeriods'

interface ExistingSubmissionPeriod {
  id: string
  startDate: string
  endDate: string
}

interface Props {
  periodId: string
  companyId: string
  submissionPeriod?: ExistingSubmissionPeriod
  onClose: () => void
  onStatusChange: (status: {
    submit: () => void
    isSubmitting: boolean
    hasErrors: boolean
  }) => void
}

const combineDateAndTime = (date: Date, time: Date): string => {
  const result = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    0
  )
  return result.toISOString()
}

const ScheduleForLater = ({
  periodId,
  companyId,
  submissionPeriod,
  onClose,
  onStatusChange,
}: Props) => {
  const isEditing = !!submissionPeriod
  const { bulkCreate, update } = useSubmissionPeriods({
    periodId,
    companyId,
    submissionPeriodId: submissionPeriod?.id,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  useEffect(() => {
    onStatusChange({ submit, isSubmitting, hasErrors })
  }, [submit, isSubmitting, hasErrors, onStatusChange])

  const schema = z.object({
    startDate: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.start_date'),
      fieldType: 'date',
      row: 'startRow',
    }),
    startTime: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.start_time'),
      fieldType: 'time',
      row: 'startRow',
    }),
    endDate: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.end_date'),
      fieldType: 'date',
      row: 'endRow',
    }),
    endTime: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.end_time'),
      fieldType: 'time',
      row: 'endRow',
    }),
  })

  const defaultValues = (() => {
    if (submissionPeriod) {
      const startDate = new Date(submissionPeriod.startDate)
      const endDate = new Date(submissionPeriod.endDate)
      return {
        startDate,
        startTime: startDate,
        endDate,
        endTime: endDate,
      }
    }
    const now = new Date()
    const oneMonthFromNow = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate())
    return {
      startDate: now,
      startTime: now,
      endDate: oneMonthFromNow,
      endTime: oneMonthFromNow,
    }
  })()

  const formDefinition = useF0FormDefinition({
    name: isEditing ? 'edit-submission-period' : 'create-submission-period',
    schema,
    defaultValues,
    onSubmit: async ({ data }) => {
      const startDate = combineDateAndTime(data.startDate, data.startTime)
      const endDate = combineDateAndTime(data.endDate, data.endTime)

      const result = isEditing
        ? await update(startDate, endDate)
        : await bulkCreate(startDate, endDate)

      if (!result.success) {
        return result
      }

      onClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return <F0Form formRef={formRef} formDefinition={formDefinition} />
}

export default ScheduleForLater
